import type { LeDataSessionSummary } from '~/types/leData';

/** The rail is a launch surface, not a complete archive. The full history is
 * still available in askRichard; this returns a scannable working set. */
export function compactRecentSessions(
  sessions: Array<LeDataSessionSummary>,
  activeSessionId: string | null,
  limit = 5,
): Array<LeDataSessionSummary> {
  const active = activeSessionId ? sessions.find((session) => session.sessionId === activeSessionId) : undefined;
  const visible = sessions.slice(0, limit);
  if (active && !visible.some((session) => session.sessionId === active.sessionId)) {
    // A title is not an identity: two investigations can start with the same
    // question and diverge. Keep the open session by id, never by a display
    // label that could hide it.
    if (visible.length < limit) {
      visible.push(active);
    } else {
      visible[visible.length - 1] = active;
    }
  }
  return visible;
}

/** Caps a frequently growing collection while preserving the currently open object. */
export function keepActiveAtLimit<T extends { id: string }>(
  items: Array<T>,
  activeId: string | null,
  limit: number,
): Array<T> {
  const visible = items.slice(0, limit);
  const active = activeId ? items.find((item) => item.id === activeId) : undefined;
  if (active && !visible.some((item) => item.id === active.id)) {
    // Same rule as compactRecentSessions: append while there is room, and only displace the
    // last row once the cap is actually reached.
    if (visible.length < limit) {
      visible.push(active);
    } else {
      visible[visible.length - 1] = active;
    }
  }
  return visible;
}
